import React, { useState, useMemo, useRef, useEffect } from "react";
import "./ActivityLogFilter.css";

/* ------------------------------------------------------------------ */
/*  Action options — `value` MUST match the backend enum, `label` is   */
/*  what the user sees. Backend choices:                               */
/*    (CREATED, "Created"), (UPDATED, "Updated"),                      */
/*    (DELETED, "Deleted"), (RETRIEVED, "Retrieved")                   */
/*  If the stored DB value is an int/other string, change `value`.     */
/* ------------------------------------------------------------------ */
const ACTION_OPTIONS = [
  { value: "CREATED", label: "Created" },
  { value: "UPDATED", label: "Updated" },
  { value: "DELETED", label: "Deleted" },
  { value: "RETRIEVED", label: "Retrieved" },
];

/* ------------------------------------------------------------------ */
/*  Date helpers                                                       */
/* ------------------------------------------------------------------ */
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const isSameDay = (a, b) =>
  !!a &&
  !!b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();
const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
// MM/DD/YYYY for the input label
const fmtDisplay = (d) =>
  d
    ? `${String(d.getMonth() + 1).padStart(2, "0")}/${String(
        d.getDate()
      ).padStart(2, "0")}/${d.getFullYear()}`
    : "";
// YYYY-MM-DD for query params (Django-friendly)
const fmtISO = (d) =>
  d
    ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
        d.getDate()
      ).padStart(2, "0")}`
    : null;

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
// Monday-first, to match the design
const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

const buildPresets = (today) => {
  const y = today.getFullYear();
  const m = today.getMonth();
  return [
    { key: "today", label: "Today", start: today, end: today },
    { key: "yesterday", label: "Yesterday", start: addDays(today, -1), end: addDays(today, -1) },
    { key: "last7", label: "Last 7 days", start: addDays(today, -6), end: today },
    { key: "last30", label: "Last 30 days", start: addDays(today, -29), end: today },
    { key: "thisMonth", label: "This Month", start: new Date(y, m, 1), end: new Date(y, m + 1, 0) },
    { key: "lastMonth", label: "Last Month", start: new Date(y, m - 1, 1), end: new Date(y, m, 0) },
  ];
};

/* ------------------------------------------------------------------ */
/*  Inline icons (no icon-lib dependency)                              */
/* ------------------------------------------------------------------ */
const IconClose = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
);
const IconChevronDown = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
);
const IconChevronLeft = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
);
const IconChevronRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
);
const IconCalendar = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
);
const IconSearch = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
);
const IconRefresh = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 0 1 15-6.7L21 8M21 3v5h-5M21 12a9 9 0 0 1-15 6.7L3 16M3 21v-5h5" /></svg>
);
const IconArrow = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 17 17 7M7 7h10v10" /></svg>
);

/* ------------------------------------------------------------------ */
/*  Range calendar — supports drag AND click-click selection           */
/* ------------------------------------------------------------------ */
function RangeCalendar({ range, onChange }) {
  const today = useMemo(() => startOfDay(new Date()), []);
  const presets = useMemo(() => buildPresets(today), [today]);

  const [viewYear, setViewYear] = useState((range.start || today).getFullYear());
  const [viewMonth, setViewMonth] = useState((range.start || today).getMonth());

  const [isSelecting, setIsSelecting] = useState(false);
  const [hover, setHover] = useState(null);
  const dragMoved = useRef(false);

  // live snapshot for the window mouseup listener (avoids stale closure)
  const snap = useRef({});
  snap.current = { isSelecting, start: range.start, hover, dragMoved };

  const finalize = (a, b) => {
    if (!a || !b) return;
    const [lo, hi] = a <= b ? [a, b] : [b, a];
    onChange({ start: lo, end: hi });
    setIsSelecting(false);
    dragMoved.current = false;
    setHover(null);
  };

  // ends a drag even if the mouse is released outside the grid
  useEffect(() => {
    const up = () => {
      const s = snap.current;
      if (s.isSelecting && s.dragMoved.current) finalize(s.start, s.hover || s.start);
    };
    window.addEventListener("mouseup", up);
    return () => window.removeEventListener("mouseup", up);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMouseDown = (day) => {
    if (!isSelecting) {
      onChange({ start: day, end: null });
      setIsSelecting(true);
      dragMoved.current = false;
      setHover(day);
    } else {
      finalize(range.start, day); // second click completes the range
    }
  };
  const handleMouseEnter = (day) => {
    if (isSelecting) {
      setHover(day);
      if (!isSameDay(day, range.start)) dragMoved.current = true;
    }
  };

  const cells = useMemo(() => {
    const first = new Date(viewYear, viewMonth, 1);
    let offset = first.getDay() - 1; // Monday-first
    if (offset < 0) offset = 6;
    const gridStart = addDays(first, -offset);
    return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  }, [viewYear, viewMonth]);

  const previewEnd = range.end || (isSelecting ? hover : null);
  const lo =
    range.start && previewEnd
      ? range.start <= previewEnd ? range.start : previewEnd
      : range.start;
  const hi =
    range.start && previewEnd
      ? range.start <= previewEnd ? previewEnd : range.start
      : null;

  const activeKey = presets.find(
    (p) => isSameDay(p.start, range.start) && isSameDay(p.end, range.end)
  )?.key;

  // year options for the dropdown: current year ±10
  const years = useMemo(() => {
    const base = today.getFullYear();
    return Array.from({ length: 21 }, (_, i) => base - 10 + i);
  }, [today]);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear((y) => y - 1); }
    else setViewMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear((y) => y + 1); }
    else setViewMonth((m) => m + 1);
  };

  return (
    <div className="alf-cal">
      <div className="alf-presets">
        {presets.map((p) => (
          <button
            key={p.key}
            className={`alf-preset ${activeKey === p.key ? "active" : ""}`}
            onClick={() => {
              onChange({ start: p.start, end: p.end });
              setViewYear(p.start.getFullYear());
              setViewMonth(p.start.getMonth());
            }}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="alf-cal-main">
        <div className="alf-cal-head">
          <button className="alf-nav" onClick={prevMonth} aria-label="Previous month"><IconChevronLeft /></button>

          <div className="alf-cal-selects">
            <div className="alf-select-wrap">
              <select
                className="alf-cal-select"
                value={viewMonth}
                onChange={(e) => setViewMonth(Number(e.target.value))}
                aria-label="Month"
              >
                {MONTHS.map((m, i) => (
                  <option key={m} value={i}>{m}</option>
                ))}
              </select>
              <span className="alf-select-caret"><IconChevronDown /></span>
            </div>

            <div className="alf-select-wrap">
              <select
                className="alf-cal-select"
                value={viewYear}
                onChange={(e) => setViewYear(Number(e.target.value))}
                aria-label="Year"
              >
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              <span className="alf-select-caret"><IconChevronDown /></span>
            </div>
          </div>

          <button className="alf-nav" onClick={nextMonth} aria-label="Next month"><IconChevronRight /></button>
        </div>

        <div className="alf-weekdays">
          {WEEKDAYS.map((d) => <span key={d}>{d}</span>)}
        </div>

        <div className="alf-cal-grid">
          {cells.map((d, i) => {
            const inView = d.getMonth() === viewMonth;
            const inRange = lo && hi && d >= lo && d <= hi;
            const isStart = isSameDay(d, lo);
            const isEnd = hi ? isSameDay(d, hi) : isSameDay(d, lo);
            const endpoint = isStart || isEnd;
            const isToday = isSameDay(d, today);

            let cellCls = "alf-cell";
            if (inRange || endpoint) cellCls += " in-range";
            if (isStart) cellCls += " range-start";
            if (isEnd) cellCls += " range-end";

            return (
              <div key={i} className={cellCls}>
                <button
                  className={`alf-day ${endpoint ? "endpoint" : ""} ${!inView ? "muted" : ""} ${isToday && !endpoint ? "today" : ""}`}
                  onMouseDown={() => handleMouseDown(d)}
                  onMouseEnter={() => handleMouseEnter(d)}
                >
                  {d.getDate()}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main drawer                                                        */
/*  Props:                                                             */
/*    open        : boolean — drawer visibility                        */
/*    onClose     : () => void                                         */
/*    value       : { actions:[], start_date, end_date } current filter*/
/*    onApply     : (payload) => void  (payload same shape as value)   */
/*    resultCount : number — shown as "Results : N"                    */
/* ------------------------------------------------------------------ */
export default function ActivityLogFilter({ open, onClose, value, onApply, resultCount }) {
  const [actions, setActions] = useState(value?.actions || []);
  const [range, setRange] = useState({
    start: value?.start_date ? new Date(value.start_date) : null,
    end: value?.end_date ? new Date(value.end_date) : null,
  });
  const [actionOpen, setActionOpen] = useState(false);
  const [calOpen, setCalOpen] = useState(false);
  const [search, setSearch] = useState("");

  // re-sync the form to the applied filters each time the drawer opens
  useEffect(() => {
    if (!open) return;
    setActions(value?.actions || []);
    setRange({
      start: value?.start_date ? new Date(value.start_date) : null,
      end: value?.end_date ? new Date(value.end_date) : null,
    });
    setActionOpen(false);
    setCalOpen(false);
    setSearch("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggleAction = (v) =>
    setActions((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));

  const filteredOptions = ACTION_OPTIONS.filter((o) =>
    o.label.toLowerCase().includes(search.toLowerCase())
  );

  const dateLabel =
    range.start && range.end
      ? `${fmtDisplay(range.start)} - ${fmtDisplay(range.end)}`
      : range.start
      ? `${fmtDisplay(range.start)} - ...`
      : "";

  const isEmpty = actions.length === 0 && !range.start && !range.end;

  const buildPayload = () => ({
    actions,
    start_date: range.start ? fmtISO(range.start) : null,
    end_date: range.end ? fmtISO(range.end) : null,
  });

  const handleApply = () => {
    onApply(buildPayload());
    onClose();
  };

  const handleReset = () => {
    setActions([]);
    setRange({ start: null, end: null });
    setSearch("");
    onApply({ actions: [], start_date: null, end_date: null });
    onClose();
  };

  const clearDate = () => setRange({ start: null, end: null });

  return (
    <>
      <div className={`alf-overlay ${open ? "show" : ""}`} onClick={onClose} />
      <aside className={`alf-drawer ${open ? "open" : ""}`} role="dialog" aria-label="Activity log filter">
        {/* Header */}
        <div className="alf-header">
          <h3>Filter</h3>
          <button className="alf-close" onClick={onClose} aria-label="Close"><IconClose /></button>
        </div>

        {/* Body */}
        <div className="alf-body">
          {/* SELECT ACTION */}
          <div className="alf-section">
            <label className="alf-label">Select Action</label>

            <div
              className={`alf-select ${actionOpen ? "active" : ""}`}
              onClick={() => setActionOpen((o) => !o)}
            >
              {actions.length === 0 ? (
                <span className="alf-placeholder">Select Action Type</span>
              ) : (
                <div className="alf-chips">
                  {actions.map((v) => {
                    const o = ACTION_OPTIONS.find((x) => x.value === v);
                    return (
                      <span className="alf-chip" key={v}>
                        {o?.label || v}
                        <span
                          onClick={(e) => { e.stopPropagation(); toggleAction(v); }}
                        >
                          ×
                        </span>
                      </span>
                    );
                  })}
                </div>
              )}
              <span className={`alf-arrow ${actionOpen ? "up" : ""}`}><IconChevronDown /></span>
            </div>

            {actionOpen && (
              <div className="alf-dropdown">
                <div className="alf-search">
                  <IconSearch />
                  <input
                    placeholder="Search Action"
                    value={search}
                    onChange={(e) => setSearch(e.target.value.trimStart())}
                  />
                </div>
                {filteredOptions.map((o) => (
                  <label className="alf-check" key={o.value}>
                    <input
                      type="checkbox"
                      checked={actions.includes(o.value)}
                      onChange={() => toggleAction(o.value)}
                    />
                    <span>{o.label}</span>
                  </label>
                ))}
                {filteredOptions.length === 0 && (
                  <div className="alf-empty">No actions found</div>
                )}
              </div>
            )}
          </div>

          {/* UPDATED ON */}
          <div className="alf-section">
            <div className="alf-section-head">
              <label className="alf-label" style={{ marginBottom: 0 }}>Updated On</label>
              <button
                className="alf-clear"
                onClick={clearDate}
                disabled={!range.start && !range.end}
              >
                Clear
              </button>
            </div>

            <div className="alf-date-input" onClick={() => setCalOpen((o) => !o)}>
              <span className={dateLabel ? "" : "alf-placeholder"}>
                {dateLabel || "Select Start date - End Date"}
              </span>
              <IconCalendar />
            </div>

            {calOpen && <RangeCalendar range={range} onChange={setRange} />}
          </div>
        </div>

        {/* Results */}
        <div className="alf-results">Results : {resultCount ?? 0}</div>

        {/* Footer */}
        <div className="alf-footer">
          <button className="alf-reset" onClick={handleReset} disabled={isEmpty}>
            Reset Filter <IconRefresh />
          </button>
          <button className="alf-apply" onClick={handleApply} disabled={isEmpty}>
            Apply Filters <IconArrow />
          </button>
        </div>
      </aside>
    </>
  );
}